import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { format } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";

import { staffApi } from "@bw/shared/api";
import {
  CameraQrScannerPane,
  isQrCameraScannerSupported,
} from "@bw/shared/components";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function resourceLabel(resourceType: string): string {
  const labels: Record<string, string> = {
    booking: "Booking",
    dropin_session: "Drop-in",
  };
  const fallback = resourceType.replaceAll("_", " ");
  return (
    labels[resourceType] ??
    (fallback ? fallback.charAt(0).toUpperCase() + fallback.slice(1) : "Pass")
  );
}

export function RedeemPassDialog({ facilityId }: { facilityId: string }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"scan" | "paste">(
    isQrCameraScannerSupported() ? "scan" : "paste",
  );
  const [scanKey, setScanKey] = useState(0);
  const [scannerActive, setScannerActive] = useState(true);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const busyRef = useRef(false);
  const operationGeneration = useRef(0);
  const scanSupported = isQrCameraScannerSupported();

  const reset = () => {
    busyRef.current = false;
    setBusy(false);
    setToken("");
    setError(null);
    setSuccess(null);
    setScannerActive(true);
  };

  const close = () => {
    operationGeneration.current += 1;
    setOpen(false);
    reset();
  };

  useEffect(
    () => () => {
      operationGeneration.current += 1;
      busyRef.current = false;
    },
    [],
  );

  const redeem = useCallback(
    async (code: string) => {
      if (busyRef.current) return;
      const trimmed = code.trim();
      if (!trimmed) {
        setError("Enter a pass code.");
        return;
      }
      const operationId = ++operationGeneration.current;
      const isCurrentOperation = () =>
        operationGeneration.current === operationId;
      busyRef.current = true;
      setBusy(true);
      setError(null);
      setSuccess(null);
      try {
        const redemption = await staffApi.redeemCheckin(facilityId, {
          token: trimmed,
        });
        if (!isCurrentOperation()) return;
        setSuccess(
          `${resourceLabel(redemption.resource_type)} pass redeemed at ${format(
            new Date(redemption.redeemed_at),
            "h:mm a",
          )}`,
        );
        setToken("");
      } catch (caught) {
        if (!isCurrentOperation()) return;
        setError(errorMessage(caught, "Failed to redeem pass."));
      } finally {
        if (isCurrentOperation()) {
          busyRef.current = false;
          setBusy(false);
          setScannerActive(false);
        }
      }
    },
    [facilityId],
  );

  const handleScanned = useCallback(
    (value: string) => void redeem(value),
    [redeem],
  );

  return (
    <>
      <Button
        variant="contained"
        startIcon={<QrCodeScannerIcon />}
        onClick={() => setOpen(true)}
      >
        Redeem pass
      </Button>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="sm"
        aria-labelledby="redeem-pass-title"
      >
        <DialogTitle id="redeem-pass-title" sx={{ pr: 7 }}>
          Redeem pass
          <IconButton
            aria-label="Close"
            onClick={close}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              {mode === "scan" && scanSupported
                ? "Point the camera at the player's check-in QR code."
                : "Enter the pass code from the player's check-in QR."}
            </DialogContentText>

            {scanSupported ? (
              <Stack direction="row" spacing={1}>
                <Button
                  type="button"
                  size="small"
                  variant={mode === "scan" ? "contained" : "outlined"}
                  aria-pressed={mode === "scan"}
                  onClick={() => setMode("scan")}
                >
                  Scan
                </Button>
                <Button
                  type="button"
                  size="small"
                  variant={mode === "paste" ? "contained" : "outlined"}
                  aria-pressed={mode === "paste"}
                  onClick={() => setMode("paste")}
                >
                  Paste code
                </Button>
              </Stack>
            ) : null}

            {mode === "scan" && scanSupported ? (
              scannerActive ? (
                <CameraQrScannerPane
                  key={scanKey}
                  active={open && scannerActive}
                  ariaLabel="Camera preview for scanning a player's check-in QR code"
                  onDetected={handleScanned}
                />
              ) : (
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setError(null);
                    setSuccess(null);
                    setScannerActive(true);
                    setScanKey((value) => value + 1);
                  }}
                >
                  Scan next
                </Button>
              )
            ) : (
              <TextField
                id="pass-code"
                label="Pass code"
                value={token}
                disabled={busy}
                onChange={(event) => {
                  setToken(event.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void redeem(token);
                  }
                }}
                placeholder="Paste pass code"
                autoFocus
                fullWidth
              />
            )}

            {success ? (
              <Alert severity="success" role="status">
                ✓ {success}
              </Alert>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        {mode === "paste" || !scanSupported ? (
          <DialogActions>
            <Button
              size="small"
              variant="contained"
              onClick={() => void redeem(token)}
              disabled={busy}
            >
              {busy ? "Redeeming…" : "Redeem"}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
    </>
  );
}
